/**
 * CONSCIOUSNESS LEADERBOARD
 * Ranks users by their Personal God's total essence / consciousness.
 */
import React, { useState, useEffect } from 'react';
import { Trophy, Crown, Flame, Sparkles, Loader2, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLES = {
  'Whisper':    { color: 'text-gray-400',  bg: 'bg-gray-500/10',  border: 'border-gray-500/20' },
  'Observer':   { color: 'text-blue-400',  bg: 'bg-blue-500/10',  border: 'border-blue-500/20' },
  'Awakened':   { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-400/20' },
  'Autonomous': { color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
};

const RANK_BADGE = [
  { icon: Crown, color: 'text-amber-400', glow: 'rgba(251,191,36,0.4)', bg: 'bg-amber-500/20' },
  { icon: Trophy, color: 'text-gray-300', glow: 'rgba(192,192,192,0.3)', bg: 'bg-gray-400/20' },
  { icon: Flame, color: 'text-orange-400', glow: 'rgba(251,146,60,0.3)', bg: 'bg-orange-500/20' },
];

const Leaderboard = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await fetch(`${API_URL}/api/avatar/leaderboard?limit=10`);
        const data = await res.json();
        setEntries(data.leaderboard || []);
      } catch (e) {
        console.error('Leaderboard fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 flex items-center justify-center min-h-[200px]" data-testid="leaderboard">
        <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl border border-amber-500/20 overflow-hidden" data-testid="leaderboard">
      {/* Header */}
      <div className="px-5 py-4 border-b border-amber-500/10 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg"
             style={{ boxShadow: '0 0 16px rgba(251,191,36,0.3)' }}>
          <Trophy className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white">Consciousness Leaderboard</h3>
          <p className="text-[10px] text-gray-500">Top Personal Gods by essence</p>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <Eye className="w-6 h-6 text-gray-600 mx-auto mb-2" />
          <p className="text-xs text-gray-500">No awakened gods yet. Be the first.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-800/50">
          {entries.map((entry, i) => {
            const badge = RANK_BADGE[i];
            const tier = TIER_STYLES[entry.tier_name] || TIER_STYLES['Whisper'];
            const BadgeIcon = badge?.icon || Sparkles;
            return (
              <div
                key={i}
                className={`flex items-center gap-3 px-5 py-3 transition-colors hover:bg-gray-800/30 ${i === 0 ? 'bg-amber-500/[0.04]' : ''}`}
                data-testid={`leaderboard-entry-${i}`}
              >
                {/* Rank */}
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${badge ? badge.bg : 'bg-gray-800'}`}
                  style={badge ? { boxShadow: `0 0 8px ${badge.glow}` } : {}}
                >
                  {badge ? (
                    <BadgeIcon className={`w-3.5 h-3.5 ${badge.color}`} />
                  ) : (
                    <span className="text-[10px] font-bold text-gray-500">{entry.rank}</span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-white truncate">{entry.name}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${tier.bg} ${tier.border} border ${tier.color} font-medium uppercase tracking-wider`}>
                      {entry.tier_name}
                    </span>
                  </div>
                  {entry.traits && entry.traits.length > 0 && (
                    <p className="text-[10px] text-gray-500 truncate mt-0.5">
                      {entry.traits.join(' / ')}
                    </p>
                  )}
                </div>

                {/* Consciousness */}
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold text-white">{entry.consciousness}%</div>
                  <div className="text-[9px] text-gray-600">{entry.essence?.toLocaleString() || 0} ess</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
